import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { Card, CardContent } from "../components/ui/card";
import CommandHeader from "../components/CommandHeader";
import { commits } from "../helpers/CommitLog";

const LANE_X = [8, 28, 48];
const NODE_Y = 22;
const CURVE_H = 40;

const curve = (fromLane, toLane) =>
    `M ${LANE_X[fromLane]} 0 C ${LANE_X[fromLane]} ${CURVE_H * 0.55}, ` +
    `${LANE_X[toLane]} ${CURVE_H * 0.25}, ${LANE_X[toLane]} ${CURVE_H}`;

const Rail = ({ rail }) => {
    const { lane, lines = {}, join, split, merge, head } = rail;

    return (
        <div className="relative w-14 shrink-0 self-stretch" aria-hidden="true">
            <svg className="absolute inset-0 h-full w-14">
                {Object.entries(lines).flatMap(([laneIndex, kind]) => {
                    const x = LANE_X[laneIndex];
                    const stroke = { className: 'stroke-border', strokeWidth: '1.5' };
                    const open = { ...stroke, strokeDasharray: '3 4', className: 'stroke-border/70' };

                    if (kind === 'dashed') {
                        return [<line key={laneIndex} x1={x} x2={x} y1={0} y2="100%" {...open} />];
                    }
                    if (kind === 'tip') {
                        return [
                            <line key={`${laneIndex}-open`} x1={x} x2={x} y1={0} y2={NODE_Y} {...open} />,
                            <line key={laneIndex} x1={x} x2={x} y1={NODE_Y} y2="100%" {...stroke} />,
                        ];
                    }
                    return [
                        <line
                            key={laneIndex}
                            x1={x}
                            x2={x}
                            y1={kind === 'down' ? NODE_Y : 0}
                            y2={kind === 'up' ? NODE_Y : '100%'}
                            {...stroke}
                        />,
                    ];
                })}
            </svg>

            {join !== undefined && (
                <svg className="absolute left-0 top-0 h-10 w-14">
                    <path
                        d={curve(join, lane)}
                        transform={`translate(0 ${NODE_Y - CURVE_H})`}
                        fill="none"
                        className="stroke-border"
                        strokeWidth="1.5"
                    />
                </svg>
            )}

            {split !== undefined && (
                <svg className="absolute bottom-0 left-0 h-10 w-14">
                    <path d={curve(lane, split)} fill="none" className="stroke-border" strokeWidth="1.5" />
                </svg>
            )}

            {head && (
                <span
                    className="absolute h-[11px] w-[11px] animate-ping rounded-full bg-primary/60 motion-reduce:animate-none"
                    style={{ left: LANE_X[lane] - 5.5, top: NODE_Y - 5.5 }}
                />
            )}
            <span
                className={
                    merge
                        ? 'absolute h-[11px] w-[11px] rounded-full border-2 border-primary bg-background'
                        : lane === 0
                            ? 'absolute h-[11px] w-[11px] rounded-full bg-primary'
                            : 'absolute h-[11px] w-[11px] rounded-full border-2 border-muted-foreground/60 bg-background'
                }
                style={{ left: LANE_X[lane] - 5.5, top: NODE_Y - 5.5 }}
            />
        </div>
    );
};

const Diff = ({ commit }) => (
    <div className="mt-3 rounded-md border border-dashed bg-muted/40 p-4 font-mono text-xs">
        <p className="text-muted-foreground">
            <span className="text-primary">$</span> git show {commit.hash}
        </p>
        {commit.paths && (
            <p className="mt-2 text-muted-foreground/80">
                {commit.paths.length} paths changed: {commit.paths.join('  ')}
            </p>
        )}
        <div className="mt-3 space-y-1.5">
            {commit.changes.map((change) => (
                <p key={change} className="flex gap-2 leading-relaxed">
                    <span className="select-none text-primary">+</span>
                    <span className="text-foreground/90">{change}</span>
                </p>
            ))}
        </div>
    </div>
);

const Experience = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const checkedOut = searchParams.get('commit');
    const [open, setOpen] = useState(() => (checkedOut ? [checkedOut] : []));
    const refs = useRef({});

    useEffect(() => {
        if (!checkedOut) return;
        setOpen((prev) => (prev.includes(checkedOut) ? prev : [...prev, checkedOut]));
        refs.current[checkedOut]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, [checkedOut]);

    const toggle = (hash) => {
        setOpen((prev) => (prev.includes(hash) ? prev.filter((item) => item !== hash) : [...prev, hash]));
        if (checkedOut === hash) {
            searchParams.delete('commit');
            setSearchParams(searchParams, { replace: true });
        }
    };

    return (
        <div className="mx-auto max-w-3xl px-6 py-12">
            <CommandHeader command="git log --graph --all" />
            <h1 className="mt-2 text-3xl font-bold tracking-tight sm:text-4xl">The path so far</h1>
            <p className="mt-3 text-muted-foreground">
                <span className="font-mono text-foreground">main</span> is the career, and it's still
                open. HEAD sits on bet365. The branches are the things that ran next to it: school,
                teaching, and the security stuff that got me here. Check out any commit to see what
                changed.
            </p>

            <p className="mt-10 font-mono text-xs text-muted-foreground">
                ↑ still open: <span className="text-primary">main</span> · mentoring/dps-student ·
                security/defcon
            </p>

            <ol className="mt-4">
                {commits.map((commit) => {
                    const expanded = open.includes(commit.hash);
                    const isCheckedOut = checkedOut === commit.hash;

                    return (
                        <li
                            key={commit.hash}
                            className="flex gap-4"
                            ref={(node) => {
                                refs.current[commit.hash] = node;
                            }}
                        >
                            <Rail rail={commit.rail} />

                            <div
                                className={`min-w-0 flex-1 pb-5 ${commit.rail.lane === 1 ? 'sm:pl-4' : ''} ${
                                    commit.rail.lane === 2 ? 'sm:pl-8' : ''
                                }`}
                            >
                                <p className="flex flex-wrap items-center gap-x-2 gap-y-1 font-mono text-xs">
                                    <span className="text-muted-foreground">{commit.hash}</span>
                                    <span className="text-muted-foreground/50">·</span>
                                    <span className="text-primary">{commit.period}</span>
                                    {commit.refs?.map((ref) => (
                                        <span
                                            key={ref}
                                            className="rounded border border-primary/40 bg-primary/10 px-1.5 py-0.5 text-[11px] text-primary"
                                        >
                                            {ref}
                                        </span>
                                    ))}
                                    {isCheckedOut && (
                                        <span className="rounded border bg-muted px-1.5 py-0.5 text-[11px] text-muted-foreground">
                                            checked out
                                        </span>
                                    )}
                                </p>

                                {commit.merge ? (
                                    <div className="mt-1">
                                        <h2 className="font-mono text-sm text-foreground">{commit.title}</h2>
                                        <p className="mt-1 text-sm text-muted-foreground">{commit.content}</p>
                                    </div>
                                ) : (
                                    <Card
                                        className={`mt-2 transition-colors ${
                                            commit.rail.lane > 0 ? 'border-dashed bg-muted/30 shadow-none' : ''
                                        } ${isCheckedOut ? 'border-primary/50' : ''}`}
                                    >
                                        <CardContent className="p-5">
                                            <h2 className="text-lg font-semibold">{commit.title}</h2>
                                            <p className="mt-2 text-sm text-muted-foreground">{commit.content}</p>

                                            <button
                                                type="button"
                                                onClick={() => toggle(commit.hash)}
                                                aria-expanded={expanded}
                                                className="mt-3 inline-flex items-center gap-1 rounded font-mono text-xs text-muted-foreground transition-colors hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                                            >
                                                <ChevronRight
                                                    className={`h-3.5 w-3.5 transition-transform ${expanded ? 'rotate-90' : ''}`}
                                                    aria-hidden="true"
                                                />
                                                git show {commit.hash}
                                            </button>

                                            {expanded && <Diff commit={commit} />}
                                        </CardContent>
                                    </Card>
                                )}
                            </div>
                        </li>
                    );
                })}
            </ol>

            <p className="mt-2 font-mono text-xs text-muted-foreground">
                # root commit. this is where it starts.
            </p>
        </div>
    );
};

export default Experience;
